import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { brandFillClass } from "./fill-variants";

export interface PageHeaderProps {
  title: string;
  description?: string;
  /** Optional leading icon (same glyph as the matching sidebar/tab entry). */
  icon?: LucideIcon;
  actions?: ReactNode;
  breadcrumb?: ReactNode;
  className?: string;
}

export function PageHeader({
  title,
  description,
  icon: Icon,
  actions,
  breadcrumb,
  className,
}: PageHeaderProps) {
  return (
    <div className={cn(className)}>
      {breadcrumb && <div className="mb-3">{breadcrumb}</div>}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-3.5">
          {Icon && title ? (
            <span
              className={cn(
                "relative hidden h-12 w-12 shrink-0 items-center justify-center rounded-xl shadow-glow ring-1 ring-inset ring-white/20 sm:flex",
                brandFillClass("primary"),
              )}
            >
              <Icon className="h-[22px] w-[22px]" aria-hidden />
            </span>
          ) : null}
          <div className="min-w-0">
            {title ? (
              <h1 className="text-balance text-2xl font-semibold tracking-[-0.025em] text-surface-900 sm:text-[28px] sm:leading-9">
                {title}
              </h1>
            ) : null}
            {description && (
              <p className="mt-1 max-w-2xl text-pretty text-sm text-surface-500 sm:text-[15px]">
                {description}
              </p>
            )}
          </div>
        </div>
        {actions && (
          <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>
        )}
      </div>
    </div>
  );
}
